import { format } from 'date-fns';
import { ClassifierData } from 'types/classifiers';
import { Task, TaskDto, TaskEventDto, TaskEventForm, TaskForm } from 'types/tasks';
import { UserData } from 'types/user';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';
import { sortByField } from 'utils/sort';

/** The classifiers the task form reads. */
export const TASK_FORM_CLASSIFIERS = [
    'TASK_PRIORITY_TYPE',
    'TASK_TYPE',
    'CORRESPONDENCE_TYPE',
    'EVENT_REMINDER_CODE',
    'CORRESPONDENCE_DIRECTION',
    'DMS_DATABASE',
];

export interface TaskFormContext {
    classifiers: Record<string, ClassifierData>;
    /** Reply-to is not stored: the form always starts it with the current user's address. */
    currentUser: UserData;
    /** The tenant's organiser, for a task the server names none for. */
    emailOrganizer: string | undefined;
}

const getTaskTypes = (
    typeCodes: string[],
    classifiers: Record<string, ClassifierData>
): { label: string; value: string }[] => {
    const selectedTypes = getOptionsFromClassifier(classifiers['TASK_TYPE']).filter(
        (val) => typeof val.value === 'string' && typeCodes.includes(val.value)
    );
    return selectedTypes as { label: string; value: string }[];
};

/** The edit form's values for a stored task. */
export const taskDtoToForm = (
    originalTask: Task,
    { classifiers, currentUser, emailOrganizer }: TaskFormContext
): TaskForm => {
    return {
        id: originalTask.id,
        // A draft may be saved before it is named.
        name: originalTask.name ?? '',
        description: originalTask.description ?? '',
        taskStatusTypeCode: originalTask.taskStatusTypeCode,
        taskPriority: (
            getOptionsFromClassifier(classifiers['TASK_PRIORITY_TYPE']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === originalTask?.taskPriorityTypeCode),
        taskTypes: getTaskTypes(originalTask.taskTypeCodes || [], classifiers),
        assignedToEmployees: sortByField(
            (originalTask.assignedToEmployees || []).map((employee) => ({
                id: employee.id,
                name: employee.name,
                firstName: employee.firstName,
                lastName: employee.lastName,
            })),
            'name'
        ),
        assignedByEmployees: sortByField(
            (originalTask.assignedByEmployees || []).map((employee) => ({
                id: employee.id,
                name: employee.name,
                firstName: employee.firstName,
                lastName: employee.lastName,
            })),
            'name'
        ),
        clientParties: sortByField([...(originalTask.clientParties || [])], 'name'),
        matters: (originalTask.matters || [])
            .map((matter) => ({
                id: matter.id,
                name: matter.name,
                fullName: matter.fullName,
                caseNumber: matter.caseNumber,
                clientParty: matter.clientParty,
                description: matter.description,
                employees: matter.employees ?? [],
                isConfidential: matter.isConfidential,
                visibility: matter.visibility,
            }))
            .sort((a, b) => a.id - b.id),
        assignments: (originalTask.assignments || []).map((assignment) => ({
            id: assignment.id,
            name: assignment.name,
            isNonBillable: assignment.isNonBillable,
            preserveNonBillableHours: assignment.preserveNonBillableHours,
            matter: assignment.matter,
        })),
        correspondence: (originalTask.correspondence || []).map(({ id, number, name, fullName }) => ({
            id,
            number,
            name,
            fullName,
        })),
        debriefings: originalTask.debriefings ?? [],
        hasDeadline: originalTask.hasDeadline,
        deadlineDate: originalTask.deadlineDate ?? null,
        isOfficial: originalTask.isOfficial ?? false,
        officialReference: originalTask.officialReference || '',
        notes: originalTask.notes || '',
        calendarSync: !!originalTask.taskEvent,
        taskEvent: taskEventDtoToForm(
            originalTask.taskEvent,
            classifiers,
            originalTask.organizerEmail ?? emailOrganizer
        ),
        dmsData: {
            dmsLink: originalTask.dmsData?.dmsLink ?? '',
            dmsDatabase:
                (
                    getOptionsFromClassifier(classifiers['DMS_DATABASE']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === originalTask?.dmsData?.dmsDatabase) ?? undefined,
            dmsNumber: originalTask.dmsData?.dmsNumber ?? '',
        },
        replyTo: [currentUser?.workEmailAddress ?? ''],
    };
};

// A task without an event still gets one, dated now, for the calendar fields to start from.
const taskEventDtoToForm = (
    originalTaskEvent: TaskEventDto | undefined,
    classifiers: Record<string, ClassifierData>,
    organizerEmail?: string
): TaskEventForm | null => {
    const isAllDay = originalTaskEvent?.isAllDayEvent ?? false;

    return {
        id: originalTaskEvent?.id,
        inviteSender: organizerEmail,
        description: originalTaskEvent?.description ?? '',
        eventLocation: originalTaskEvent?.eventLocation ?? '',
        allDayMeeting: isAllDay,
        reminder: originalTaskEvent?.reminder
            ? ((
                  getOptionsFromClassifier(classifiers['EVENT_REMINDER_CODE']) as {
                      label: string;
                      value: string;
                  }[]
              ).find((val) => val.value === originalTaskEvent.reminder) ?? null)
            : null,
        dateStart: originalTaskEvent?.dateStart
            ? format(new Date(originalTaskEvent.dateStart), DEFAULT_DATE_FORMAT)
            : format(new Date(), DEFAULT_DATE_FORMAT),
        timeStart: isAllDay ? null : originalTaskEvent?.dateStart ? new Date(originalTaskEvent.dateStart) : new Date(),
        dateEnd: originalTaskEvent?.dateEnd
            ? format(new Date(originalTaskEvent.dateEnd), DEFAULT_DATE_FORMAT)
            : format(new Date(), DEFAULT_DATE_FORMAT),
        timeEnd: isAllDay ? null : originalTaskEvent?.dateEnd ? new Date(originalTaskEvent.dateEnd) : new Date(),
        eventInvitees: (originalTaskEvent?.eventInvitees ?? []).map((eventInvitee) => {
            return { id: eventInvitee.employeeId, name: eventInvitee.employeeName ?? '' };
        }),
        eventId: originalTaskEvent?.eventId ?? null,
    };
};

/**
 * The create / update request for the form's values. The form always carries an event, so it goes only with
 * calendar sync on; it is mapped either way, and one without an organiser throws.
 */
export const taskFormToRequest = (formData: TaskForm): TaskDto => {
    const request: TaskDto = {
        id: formData.id,
        name: formData.name,
        description: formData.description,
        taskPriorityTypeCode: formData.taskPriority?.value,
        taskStatusTypeCode: formData.taskStatusTypeCode,
        clientParties: formData.clientParties ? formData.clientParties.map(({ id }) => ({ id })) : [],
        taskTypeCodes: formData.taskTypes?.map((type) => type.value) ?? [],
        assignedToEmployees: (formData.assignedToEmployees ?? []).map(({ id }) => ({ id })),
        assignedByEmployees: (formData.assignedByEmployees ?? []).map(({ id }) => ({ id })),
        matters: (formData.matters ?? []).map(({ id }) => ({ id: id })),
        assignments: (formData.assignments || []).map(({ id }) => ({ id })),
        correspondence: (formData.correspondence || []).map(({ id }) => ({ id })),
        debriefings: (formData.debriefings || []).map(({ id }) => ({ id })),
        hasDeadline: formData.hasDeadline ?? false,
        deadlineDate: formData.deadlineDate,
        isOfficial: formData.isOfficial,
        officialReference: formData.officialReference || null,
        notes: formData.notes || null,
        taskEvent: formData.taskEvent ? taskEventFormToDto(formData.taskEvent) : null,
        dmsData: {
            dmsLink: formData.dmsData?.dmsLink ?? '',
            dmsDatabase: formData.dmsData?.dmsDatabase?.value ?? '',
            dmsNumber: formData.dmsData?.dmsNumber ?? '',
        },
        replyTo: formData.replyTo ?? [],
    };
    if (!formData.calendarSync) {
        request.taskEvent = null;
    }
    return request;
};

const taskEventFormToDto = (taskEventFormData: TaskEventForm): TaskEventDto => {
    if (!taskEventFormData.inviteSender) {
        throw new Error('inviteSender required');
    }
    let dateStart: Date | null = null;
    let dateEnd: Date | null = null;

    if (taskEventFormData.allDayMeeting) {
        // convert local day to UTC day as is, so 5th day from local time would be 5th day in UTC
        dateStart = taskEventFormData.dateStart
            ? new Date(
                  Date.UTC(
                      new Date(taskEventFormData.dateStart).getFullYear(),
                      new Date(taskEventFormData.dateStart).getMonth(),
                      new Date(taskEventFormData.dateStart).getDate(),
                      taskEventFormData.timeStart?.getHours() || 0,
                      taskEventFormData.timeStart?.getMinutes() || 0,
                      0,
                      0
                  )
              )
            : null;
        dateEnd = taskEventFormData.dateEnd
            ? new Date(
                  Date.UTC(
                      new Date(taskEventFormData.dateEnd).getFullYear(),
                      new Date(taskEventFormData.dateEnd).getMonth(),
                      new Date(taskEventFormData.dateEnd).getDate(),
                      taskEventFormData.timeEnd?.getHours() || 0,
                      taskEventFormData.timeEnd?.getMinutes() || 0,
                      0,
                      0
                  )
              )
            : null;
    } else {
        dateStart = taskEventFormData.dateStart
            ? new Date(
                  new Date(taskEventFormData.dateStart).setHours(
                      taskEventFormData.timeStart?.getHours() || 0,
                      taskEventFormData.timeStart?.getMinutes() || 0,
                      0,
                      0
                  )
              )
            : null;
        dateEnd = taskEventFormData.dateEnd
            ? new Date(
                  new Date(taskEventFormData.dateEnd).setHours(
                      taskEventFormData.timeEnd?.getHours() || 0,
                      taskEventFormData.timeEnd?.getMinutes() || 0,
                      0
                  )
              )
            : null;
    }

    return {
        id: taskEventFormData.id,
        isAllDayEvent: taskEventFormData.allDayMeeting ?? false,
        inviteSender: taskEventFormData.inviteSender,
        description: taskEventFormData.description,
        eventLocation: taskEventFormData.eventLocation,
        reminder: taskEventFormData.reminder?.value ?? null,
        dateStart: dateStart,
        dateEnd: dateEnd,
        eventInvitees: (taskEventFormData.eventInvitees ?? []).map((evenInvitee) => {
            return { employeeId: evenInvitee.id, employeeName: evenInvitee.name };
        }),
    };
};

/**
 * The form fields each task section edits, by its `?edit=` id. The event goes whole with the calendar section,
 * as does reply-to, which only its invite uses. Status is the header's switch, so no section sends it.
 */
export const TASK_SECTION_FIELDS = {
    general: ['taskTypes', 'name', 'description', 'taskPriority', 'isOfficial', 'officialReference'],
    assignees: ['assignedToEmployees', 'assignedByEmployees'],
    connections: ['clientParties', 'matters', 'assignments', 'correspondence', 'debriefings'],
    'deadline-calendar': ['hasDeadline', 'deadlineDate', 'calendarSync', 'taskEvent', 'replyTo'],
    dms: ['dmsData'],
    notes: ['notes'],
} satisfies Record<string, (keyof TaskForm)[]>;

export type TaskSectionId = keyof typeof TASK_SECTION_FIELDS;

const typeCodes = (form: TaskForm) =>
    (form.taskTypes ?? [])
        .map(({ value }) => value)
        .sort()
        .join();

/**
 * The request for one section's save: `edited` is the task as stored now with that section's fields in place. The
 * calendar event's derived values follow their inputs as the full form's handlers keep them: invitees gain the
 * assignees added and lose those removed, and an event not yet sent takes the organiser the section's form resolved
 * for its new types or Official flag. Without calendar sync no event goes, as on the full form.
 */
export const taskSectionToRequest = (
    edited: TaskForm,
    latest: TaskForm,
    resolvedOrganizer: string | null | undefined
): TaskDto => {
    const event = edited.taskEvent;
    if (!edited.calendarSync || !event) {
        return taskFormToRequest(edited);
    }
    const before = latest.assignedToEmployees ?? [];
    const after = edited.assignedToEmployees ?? [];
    const removedIds = new Set(before.filter(({ id }) => !after.some((e) => e.id === id)).map(({ id }) => id));
    const invitees = (event.eventInvitees ?? []).filter(({ id }) => !removedIds.has(id));
    after
        .filter(({ id }) => !before.some((e) => e.id === id) && !invitees.some((e) => e.id === id))
        .forEach(({ id, name }) => invitees.push({ id, name }));

    const organizerInputsChanged =
        typeCodes(latest) !== typeCodes(edited) || !!latest.isOfficial !== !!edited.isOfficial;
    const inviteSender =
        organizerInputsChanged && event.eventId == null && resolvedOrganizer ? resolvedOrganizer : event.inviteSender;

    return taskFormToRequest({ ...edited, taskEvent: { ...event, eventInvitees: invitees, inviteSender } });
};

/**
 * Whether the record page offers Edit, as the server takes an update: never on an automatic task, with
 * UPDATE.TASKS.ALL or MY_OWN on any other, and with UPDATE.TASKS.CREATED_BY_ME only on a task the user created.
 */
export const canEditTaskInPlace = (
    task: Pick<Task, 'isAutomatic' | 'createdByEmployee'>,
    userId: number | undefined,
    { canUpdate, canUpdateCreated }: { canUpdate: boolean; canUpdateCreated: boolean }
): boolean =>
    !task.isAutomatic && (canUpdate || (canUpdateCreated && userId != null && task.createdByEmployee?.id === userId));
